export interface ICalendarDayPositionedEvent {
    startIndex: number;
    endIndex: number;
}

export interface ICalendarDayEventColumnsLayout {
    eventCount: number;
    groupCount: number;
    eventsBefore: number;
    groupsBefore: number;
    maxGroupedEventCount: number;
}

export type ICalendarDayDisplayedEvent<TEvent extends ICalendarDayPositionedEvent> = TEvent & {
    lane: number;
    columnsLayout: ICalendarDayEventColumnsLayout;
}

export interface ICalendarDayEventGroup<
    TEvent extends ICalendarDayPositionedEvent,
    TStatus,
> {
    startIndex: number;
    lane: number;
    durationSlots: number;
    status: TStatus;
    events: TEvent[];
    columnsLayout: ICalendarDayEventColumnsLayout;
}

const getOverlappingEventGroups = <TEvent extends ICalendarDayPositionedEvent>(
    events: TEvent[]
): TEvent[][] => {
    const groups: {endIndex: number; events: TEvent[]}[] = []
    const sortedEvents = [...events].sort((first, second) => (
        first.startIndex - second.startIndex ||
        second.endIndex - first.endIndex
    ))

    sortedEvents.forEach(event => {
        const currentGroup = groups.at(-1)

        if(currentGroup && event.startIndex < currentGroup.endIndex){
            currentGroup.events.push(event)
            currentGroup.endIndex = Math.max(currentGroup.endIndex, event.endIndex)
            return
        }

        groups.push({
            endIndex: event.endIndex,
            events: [event],
        })
    })

    return groups.map(group => group.events)
}

export const getCalendarDayEventLayout = <
    TEvent extends ICalendarDayPositionedEvent,
    TStatus extends string,
>(
    positionedEvents: TEvent[],
    statusOrder: readonly TStatus[],
    getStatus: (event: TEvent) => TStatus,
    groupSingleEvents = false,
): {
    visibleEvents: ICalendarDayDisplayedEvent<TEvent>[];
    groupedEvents: ICalendarDayEventGroup<TEvent, TStatus>[];
} => {
    const visibleEvents: ICalendarDayDisplayedEvent<TEvent>[] = []
    const groupedEvents: ICalendarDayEventGroup<TEvent, TStatus>[] = []

    getOverlappingEventGroups(positionedEvents).forEach(overlappingEvents => {
        const statuses = [...new Set(overlappingEvents.map(getStatus))]
            .sort((first, second) => {
                const firstIndex = statusOrder.indexOf(first)
                const secondIndex = statusOrder.indexOf(second)

                return (firstIndex === -1 ? statusOrder.length : firstIndex) -
                    (secondIndex === -1 ? statusOrder.length : secondIndex)
            })
        const statusLayouts = statuses.map(status => {
            const events = overlappingEvents.filter(event => getStatus(event) === status)

            return {
                status,
                events,
                isGrouped: groupSingleEvents || events.length > 1,
            }
        })
        const eventCount = statusLayouts.filter(statusLayout => !statusLayout.isGrouped).length
        const groupCount = statusLayouts.length - eventCount
        const maxGroupedEventCount = Math.max(
            0,
            ...statusLayouts.map(statusLayout => (
                statusLayout.isGrouped ? statusLayout.events.length : 0
            ))
        )
        let eventsBefore = 0
        let groupsBefore = 0

        statusLayouts.forEach(({status, events, isGrouped}) => {
            const lane = eventsBefore + groupsBefore
            const columnsLayout: ICalendarDayEventColumnsLayout = {
                eventCount,
                groupCount,
                eventsBefore,
                groupsBefore,
                maxGroupedEventCount,
            }

            if(!isGrouped){
                visibleEvents.push({
                    ...events[0],
                    lane,
                    columnsLayout,
                })
                eventsBefore++
                return
            }

            const startIndex = Math.min(...events.map(event => event.startIndex))
            const endIndex = Math.max(...events.map(event => event.endIndex))

            groupedEvents.push({
                startIndex,
                lane,
                durationSlots: endIndex - startIndex,
                status,
                events,
                columnsLayout,
            })
            groupsBefore++
        })
    })

    return {
        visibleEvents,
        groupedEvents,
    }
}
